/**
 * WPHealthWarningDialog - Shown before content creation when WP site is unreachable
 *
 * When the pre-flight health check detects the WordPress site is down,
 * warns the user that internal links will be skipped. User can proceed
 * ("Generate Anyway") or cancel.
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';

interface WPHealthWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProceedAnyway: () => void;
  tenantId: string;
}

export const WPHealthWarningDialog: React.FC<WPHealthWarningDialogProps> = ({
  open,
  onOpenChange,
  onProceedAnyway,
  tenantId,
}) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle size={20} className="text-coral" />
            WordPress site unreachable
          </AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block mt-1 text-muted-foreground">
              We couldn't connect to <strong>{tenantId}</strong>.
            </span>
          </AlertDialogDescription>
          <div className="rounded-md border border-border bg-coral-light p-3 space-y-1 text-sm text-foreground">
            <p>The article will be generated <strong>without internal links</strong> to your site.</p>
            <p>Internal links will need to be added manually after your site is back online.</p>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <button
            className="inline-flex items-center justify-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </button>
          <button
            className="inline-flex items-center justify-center rounded-md bg-coral px-4 py-2 text-sm font-medium text-white hover:bg-coral-hover"
            onClick={() => {
              onOpenChange(false);
              onProceedAnyway();
            }}
          >
            Generate Anyway
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default WPHealthWarningDialog;
